import React, { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import {
  selectEmailResponseCitationIsLoading,
  selectEmailResponsePollingEnabled,
  setEmailResponse,
  setEmailResponseAiAgentId,
  setEmailResponseCitationLoading,
  setEmailResponseCitations,
} from '../../../store/emailHub/emailResponse'
import { AIAgentType } from '../../../models/AIAgent'
import { selectEmailMetadataPollingEnabled, updateEmailMetadata } from '../../../store/emailHub/emailMetadata'
import { useLazyGetEmailMetadataQuery } from '../../../store/integrations/emailMetadata'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { EmailResponseResult } from '../../../models/EmailResponse'
import { parseMetadata } from '../../../apps/professional-app/fa/MeetingSummary/utils'
import { isEmailResponseNoResult } from '../EmailResponse/utils'
import { setMessages } from '../../../store/streamingChat/streamingChat'

export interface EmailResponsePollerProps {
  emailId: string
}

export const EmailResponsePoller: React.FC<EmailResponsePollerProps> = ({ emailId }) => {
  usePollEmailResponse(emailId)
  usePollEmailMetadata(emailId)
  usePollEmailResponseCitations(emailId)

  return null
}

const usePollEmailResponse = (emailId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const emailResponsePollingEnabled = useAppSelector(selectEmailResponsePollingEnabled)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const emailConnector = emailIntegration?.serviceId ?? null

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const getEmailResponse = useCallback(async () => {
    const { data: aiAgentResult } = await triggerGetAiAgentResult({
      accountId,
      type: AIAgentType.EMAIL_RESPONSE_V2,
      resultKey: `${currentUserId}|${emailConnector}|${emailId}`,
    })
    if (!aiAgentResult) {
      return
    }
    const { originalAiAgentId, result, interaction, metadata } = aiAgentResult
    const { citations, startedGenerating, emailSignatureProcessed } = parseMetadata(metadata)
    if (!isEmailResponseNoResult(result as EmailResponseResult) && !emailSignatureProcessed) {
      return
    }
    const { aiAgentId, interactions = [] } = interaction ?? {}
    dispatch(setEmailResponseAiAgentId(aiAgentId ?? originalAiAgentId))
    dispatch(setEmailResponse(result as EmailResponseResult))
    dispatch(setMessages(interactions.map((i) => i.result)))
    if (citations) {
      dispatch(setEmailResponseCitations(citations))
    } else if (startedGenerating) {
      dispatch(setEmailResponseCitationLoading(true))
    }
  }, [dispatch, accountId, currentUserId, emailConnector, emailId, triggerGetAiAgentResult])

  const { start: startEmailResponsePolling, stop: stopEmailResponsePolling } = usePollingEffect(getEmailResponse, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (emailResponsePollingEnabled) {
      startEmailResponsePolling()
    } else {
      stopEmailResponsePolling()
    }
  }, [emailResponsePollingEnabled, startEmailResponsePolling, stopEmailResponsePolling])
}

const usePollEmailMetadata = (emailId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const emailMetadataPollingEnabled = useAppSelector(selectEmailMetadataPollingEnabled)

  const [triggerGetEmailMetadata] = useLazyGetEmailMetadataQuery()

  const getEmailMetadata = useCallback(async () => {
    if (!emailIntegration) {
      return
    }
    const emailMetadata = await triggerGetEmailMetadata({
      accountId,
      emailConnector: emailIntegration.serviceId,
      externalId: emailId,
    }).unwrap()
    if (!emailMetadata) {
      return
    }
    dispatch(updateEmailMetadata(emailMetadata))
  }, [dispatch, accountId, emailIntegration, emailId, triggerGetEmailMetadata])

  const { start: startEmailMetadataPolling, stop: stopEmailMetadataPolling } = usePollingEffect(getEmailMetadata, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (emailMetadataPollingEnabled) {
      startEmailMetadataPolling()
    } else {
      stopEmailMetadataPolling()
    }
  }, [emailMetadataPollingEnabled, startEmailMetadataPolling, stopEmailMetadataPolling])
}

const usePollEmailResponseCitations = (emailId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const citationIsLoading = useAppSelector(selectEmailResponseCitationIsLoading)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const emailConnector = emailIntegration?.serviceId ?? null

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  const getCitations = useCallback(async () => {
    if (!emailConnector) {
      return
    }
    const { data: aiAgentResult } = await triggerGetAiAgentResult({
      accountId,
      type: AIAgentType.EMAIL_RESPONSE_V2,
      resultKey: `${currentUserId}|${emailConnector}|${emailId}`,
    })
    if (!aiAgentResult) {
      dispatch(setEmailResponseCitationLoading(false))
      return
    }
    const { result, metadata } = aiAgentResult
    const { citations } = parseMetadata(metadata)
    if (!citations) {
      return
    }
    dispatch(setEmailResponseCitations(citations))
    dispatch(setEmailResponse(result as EmailResponseResult))
    dispatch(setEmailResponseCitationLoading(false))
  }, [dispatch, accountId, currentUserId, emailConnector, emailId, triggerGetAiAgentResult])

  const { start: startEmailResponseCitationPolling, stop: stopEmailResponseCitationPolling } = usePollingEffect(getCitations, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (citationIsLoading) {
      startEmailResponseCitationPolling()
    } else {
      stopEmailResponseCitationPolling()
    }
  }, [citationIsLoading, startEmailResponseCitationPolling, stopEmailResponseCitationPolling])
}
